/**
 * Hybrid Logical Clock fuer die Synchronisierung (T-04).
 *
 * Jede Feldaenderung bekommt einen Zeitstempel aus Uhrzeit, Zaehler und
 * Geraet. Als Text mit fester Breite lassen sich zwei Stempel einfach
 * vergleichen (`a > b` = a ist neuer), und es gibt nie zwei gleiche Stempel
 * von verschiedenen Geraeten. Geht die Uhr eines Geraets nach, zieht `sehen`
 * sie auf den neuesten bekannten Stand - eine spaetere Aenderung ist damit
 * immer auch die neuere.
 */
export type Uhr = string;

/** Aelter als jede echte Aenderung - fuer Bestandsdaten bei der Einrichtung. */
export const UHR_NULL: Uhr = '000000000.0000.';

const MS_BREITE = 9;
const Z_BREITE = 4;

export function uhrText(ms: number, z: number, geraet: string): Uhr {
  return `${Math.max(0, Math.floor(ms)).toString(36).padStart(MS_BREITE, '0')}.${z.toString(36).padStart(Z_BREITE, '0')}.${geraet}`;
}

export function uhrLesen(u: Uhr): { ms: number; z: number; geraet: string } {
  const [ms = '0', z = '0', ...rest] = u.split('.');
  return { ms: parseInt(ms, 36) || 0, z: parseInt(z, 36) || 0, geraet: rest.join('.') };
}

export const neuere = (a: Uhr | undefined, b: Uhr | undefined): Uhr | undefined =>
  a === undefined ? b : b === undefined ? a : a > b ? a : b;

/** Die Uhr eines Geraets. Haelt den letzten Stand nur im Speicher; `sehen` holt fremde Staende ein. */
export class Uhrwerk {
  private ms = 0;
  private z = 0;

  constructor(readonly geraet: string, letzte?: Uhr) {
    if (letzte) this.sehen(letzte);
  }

  /** Neuer Stempel, garantiert spaeter als alles bisher Gesehene. */
  tick(jetzt = Date.now()): Uhr {
    if (jetzt > this.ms) {
      this.ms = jetzt;
      this.z = 0;
    } else {
      this.z += 1;
    }
    return uhrText(this.ms, this.z, this.geraet);
  }

  /** Einen fremden Stempel zur Kenntnis nehmen. */
  sehen(fremd: Uhr): void {
    const { ms, z } = uhrLesen(fremd);
    if (ms > this.ms || (ms === this.ms && z > this.z)) {
      this.ms = ms;
      this.z = z;
    }
  }

  get stand(): Uhr {
    return uhrText(this.ms, this.z, this.geraet);
  }
}
